import React, { useState } from 'react';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { Actions as LEGAL_ACTIONS } from '../../store/actions/legalActions';
import ValidationMessage from '../utils/ValidationMessage';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import _ from 'lodash';

const validationSchema = yup.object().shape({
  signatoryEmail: yup.string().email('Please enter a valid email address').required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
});

const LegalSignatoryModal = ({
  open,
  onClose,
  documentKinds,
  requestLegalSignatory,
}) => {
  const [form, setForm] = useState({ signatoryEmail: '', firstName: '', lastName: '', message: '' });
  const [validationError, setValidationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setValidationError(null);
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(form, { abortEarly: false });
    } catch (error) {
      setValidationError(error.inner);
      return;
    }

    setSubmitting(true);
    const result = await requestLegalSignatory(
      form.signatoryEmail,
      documentKinds,
      form.message || undefined,
      form.firstName,
      form.lastName
    );
    setSubmitting(false);

    if (result) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='max-w-md'>
          <h4 className='text-lg font-semibold mb-4'>Request Sent</h4>
          <p className='text-sm text-muted-foreground mb-4'>
            We have sent a request to <strong>{form.signatoryEmail}</strong> to review and accept the agreements on behalf of your organisation.
          </p>
          <Button onClick={onClose}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-w-md'>
        <h4 className='text-lg font-semibold mb-4'>Invite Legal Signatory</h4>
        <p className='text-sm text-muted-foreground mb-4'>
          You do not have permission to accept these agreements. Please invite someone with the appropriate authority to review and sign.
        </p>

        <div className='space-y-3'>
          <div>
            <label className='text-sm font-medium mb-1 block'>First Name</label>
            <input
              type='text'
              value={form.firstName}
              onChange={handleChange('firstName')}
              className='w-full border border-border rounded px-3 py-2 text-sm bg-input'
              placeholder='First name'
            />
            <ValidationMessage message={_.find(validationError, { path: 'firstName' })?.message} />
          </div>

          <div>
            <label className='text-sm font-medium mb-1 block'>Last Name</label>
            <input
              type='text'
              value={form.lastName}
              onChange={handleChange('lastName')}
              className='w-full border border-border rounded px-3 py-2 text-sm bg-input'
              placeholder='Last name'
            />
            <ValidationMessage message={_.find(validationError, { path: 'lastName' })?.message} />
          </div>

          <div>
            <label className='text-sm font-medium mb-1 block'>Email Address</label>
            <input
              type='email'
              value={form.signatoryEmail}
              onChange={handleChange('signatoryEmail')}
              className='w-full border border-border rounded px-3 py-2 text-sm bg-input'
              placeholder='signatory@company.com'
            />
            <ValidationMessage message={_.find(validationError, { path: 'signatoryEmail' })?.message} />
          </div>

          <div>
            <label className='text-sm font-medium mb-1 block'>Message (optional)</label>
            <textarea
              value={form.message}
              onChange={handleChange('message')}
              className='w-full border border-border rounded px-3 py-2 text-sm bg-input min-h-[80px]'
              placeholder='Optional message to include in the request...'
            />
          </div>
        </div>

        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='outline' onClick={onClose}>Cancel</Button>
          <Button loading={submitting} onClick={handleSubmit}>Send Request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => ({
  requestLegalSignatory: (email, kinds, msg, first, last) =>
    dispatch(LEGAL_ACTIONS.requestLegalSignatory(email, kinds, msg, first, last)),
});

export default connect(null, mapDispatchToProps)(LegalSignatoryModal);
